import React, { useEffect, useId, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

const AccessibleModal = ({
  title,
  onClose,
  children,
  footer,
  maxWidth,
  initialFocusRef,
}) => {
  const { t } = useTranslation();
  const titleId = useId();
  const overlayRef = useRef(null);
  const dialogRef = useRef(null);
  const previousActiveRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    previousActiveRef.current = document.activeElement;
    const dialog = dialogRef.current;
    const focusTarget = initialFocusRef?.current || dialog?.querySelector(FOCUSABLE);
    focusTarget?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab' || !dialog) return;
      const focusables = dialog.querySelectorAll(FOCUSABLE);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = originalOverflow;
      previousActiveRef.current?.focus?.();
    };
  }, [initialFocusRef, onClose]);

  return (
    <div
      ref={overlayRef}
      className="modal-overlay"
      onMouseDown={(event) => {
        if (event.target === overlayRef.current) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className={`modal${isExpanded ? ' modal-expanded' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={maxWidth ? { maxWidth } : undefined}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="modal-header">
          <span id={titleId} className="modal-title">{title}</span>
          <div className="modal-header-actions">
            <button
              type="button"
              className="modal-expand"
              aria-pressed={isExpanded}
              aria-label={isExpanded ? t('common.reduceWindow') : t('common.expandWindow')}
              onClick={() => setIsExpanded((current) => !current)}
            >
              {isExpanded ? t('common.reduce') : t('common.expand')}
            </button>
            <button type="button" className="modal-close" aria-label={t('common.close')} onClick={onClose}>✕</button>
          </div>
        </div>
        <div className="modal-body">{children}</div>
        {footer ? <div className="modal-footer">{footer}</div> : null}
      </div>
    </div>
  );
};

export default AccessibleModal;
